import { createContext, useContext, useReducer, useEffect } from 'react'

const AuthContext = createContext()

const authReducer = (state, action) => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload }
    case 'SET_USER':
      return { ...state, user: action.payload, loading: false }
    case 'SET_COMPANY':
      return { ...state, company: action.payload }
    case 'CLEAR_AUTH':
      return { ...state, user: null, company: null, loading: false }
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false }
    default:
      return state
  }
}

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(authReducer, {
    user: null,
    company: null,
    loading: true,
    error: null
  })

  useEffect(() => {
    const initAuth = async () => {
      try {
        console.log('🔐 === INITIALISATION AUTHENTIFICATION APPWRITE ===')
        
        // Attendre que Appwrite soit prêt avec timeout
        let attempts = 0
        while (!window.appwriteAuth && attempts < 100) {
          await new Promise(resolve => setTimeout(resolve, 100))
          attempts++
        }
        
        if (!window.appwriteAuth) {
          console.error('❌ Appwrite Auth non disponible après timeout')
          dispatch({ type: 'SET_ERROR', payload: 'Service d\'authentification non disponible' })
          return
        }

        console.log('✅ Appwrite Auth détecté')

        // Attendre que Appwrite soit complètement initialisé
        let appwriteReady = false
        attempts = 0
        while (!appwriteReady && attempts < 50) {
          try {
            if (window.appwriteAuth.account) {
              try {
                const session = await window.appwriteAuth.account.get()
                appwriteReady = true
                console.log('✅ Appwrite complètement initialisé')
                
                // Vérifier session existante
                if (session) {
                  console.log('🔍 Session existante trouvée:', session.email)
                  
                  // Charger le profil utilisateur si pas déjà chargé
                  if (!window.appwriteAuth.currentUser) {
                    console.log('👤 Chargement profil utilisateur...')
                    await window.appwriteAuth.loadUserProfile()
                  }
                  
                  const user = window.appwriteAuth.getUser()
                  const company = window.appwriteAuth.getCompany()
                  
                  if (user) {
                    console.log('✅ Utilisateur connecté:', user.email)
                    dispatch({ type: 'SET_USER', payload: user })
                    dispatch({ type: 'SET_COMPANY', payload: company })
                  } else {
                    console.log('⚠️ Session trouvée mais profil non chargé')
                    dispatch({ type: 'SET_LOADING', payload: false })
                  }
                } else {
                  console.log('📭 Aucune session active')
                  dispatch({ type: 'SET_LOADING', payload: false })
                }
              } catch (sessionError) {
                // Pas de session active - normal
                console.log('📭 Pas de session active')
                appwriteReady = true
                dispatch({ type: 'SET_LOADING', payload: false })
              }
            } else {
              await new Promise(resolve => setTimeout(resolve, 100))
              attempts++
            }
          } catch (error) {
            console.log('⏳ Attente initialisation Appwrite...', attempts)
            await new Promise(resolve => setTimeout(resolve, 100))
            attempts++
          }
        }
        
        if (!appwriteReady) {
          console.error('❌ Timeout initialisation Appwrite')
          dispatch({ type: 'SET_ERROR', payload: 'Timeout d\'initialisation' })
        }
        
      } catch (error) {
        console.error('❌ Erreur init auth:', error)
        dispatch({ type: 'SET_ERROR', payload: error.message })
      }
    }

    initAuth()
  }, [])

  const signIn = async (email, password) => {
    console.log('🔑 === TENTATIVE DE CONNEXION APPWRITE ===')
    console.log('📧 Email:', email)
    
    dispatch({ type: 'SET_LOADING', payload: true })
    dispatch({ type: 'SET_ERROR', payload: null })
    
    try {
      if (!window.appwriteAuth) {
        throw new Error('Service d\'authentification non disponible')
      }

      console.log('📞 Appel signIn sur appwriteAuth...')
      const result = await window.appwriteAuth.signIn(email, password)
      
      console.log('📋 Résultat signIn:', result)
      
      if (result.success) {
        console.log('✅ Connexion réussie!')
        
        // Attendre un peu que les données soient chargées
        await new Promise(resolve => setTimeout(resolve, 500))
        
        const user = window.appwriteAuth.getUser()
        const company = window.appwriteAuth.getCompany()
        
        console.log('👤 User récupéré:', user)
        console.log('🏢 Company récupérée:', company)
        
        if (user) {
          dispatch({ type: 'SET_USER', payload: user })
          dispatch({ type: 'SET_COMPANY', payload: company })
          return { success: true }
        } else {
          console.error('❌ User null après connexion réussie')
          dispatch({ type: 'SET_ERROR', payload: 'Erreur de récupération du profil' })
          return { success: false, error: 'Erreur de récupération du profil' }
        }
      } else {
        console.error('❌ Échec connexion:', result.error)
        dispatch({ type: 'SET_ERROR', payload: result.error })
        return result
      }
    } catch (error) {
      console.error('❌ Erreur signIn:', error)
      dispatch({ type: 'SET_ERROR', payload: error.message })
      return { success: false, error: error.message }
    }
  }

  const signUp = async (email, password, fullName, companyName) => {
    dispatch({ type: 'SET_LOADING', payload: true })
    dispatch({ type: 'SET_ERROR', payload: null })
    
    try {
      if (!window.appwriteAuth) {
        throw new Error('Service d\'authentification non disponible')
      }

      const result = await window.appwriteAuth.signUp(email, password, fullName, companyName)
      
      if (result.success && !result.needsConfirmation) {
        const user = window.appwriteAuth.getUser()
        const company = window.appwriteAuth.getCompany()
        
        dispatch({ type: 'SET_USER', payload: user })
        dispatch({ type: 'SET_COMPANY', payload: company })
      } else {
        dispatch({ type: 'SET_LOADING', payload: false })
      }
      
      return result
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message })
      return { success: false, error: error.message }
    }
  }

  const signOut = async () => {
    try {
      if (window.appwriteAuth) {
        await window.appwriteAuth.signOut()
      }
      dispatch({ type: 'CLEAR_AUTH' })
    } catch (error) {
      console.error('❌ Erreur déconnexion:', error)
    }
  }

  return (
    <AuthContext.Provider value={{
      ...state,
      signIn,
      signUp,
      signOut
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}